"use client";

import React, { useMemo, useState, useEffect } from "react";

import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Info, ArrowLeft } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import TwoFA from "./MainTwoFA";
import { SecuredFooter } from "./ui/secured-footer";

const SendConfirmation = ({
  asset,
  destinationAddress,
  onCancel,
  disableBtn,
  onConfirm,
  setUp,
  quoteData,
  isConfirming,
  zar,
  currency,
  currentRate,
  amount,
}) => {
  const [open, setOpen] = useState(false);
  const [isTwoFAOpen, setIsTwoFAOpen] = useState(false);
  const [isEnabled, setIsEnabled] = useState(false);
  const { settings } = useAuth();

  useEffect(() => {
    setIsEnabled(settings?.security?.twoFactorAuth?.enabled);
  }, [settings]);

  const formattedConvertedValue = useMemo(() => {
    const localCurrency = currency?.toUpperCase() || "ZAR";

    const formatFiat = (value) => {
      if (value === undefined || value === null || value === "") {
        return null;
      }

      const parsedValue = Number(value);
      if (Number.isNaN(parsedValue)) {
        return null;
      }

      // Use period as decimal separator (e.g. R 120.00) for consistent display
      const formatted = parsedValue.toFixed(2);
      if (localCurrency === "ZAR") {
        return `R ${formatted}`;
      }
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: localCurrency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(parsedValue);
    };

    const cryptoAmount =
      quoteData?.amountReceived ?? (amount !== undefined ? amount : null);

    return (
      formatFiat(zar) ||
      formatFiat(
        quoteData?.fiatAmount ??
          (cryptoAmount && currentRate
            ? Number(cryptoAmount) * Number(currentRate)
            : null)
      ) ||
      formatFiat(0)
    );
  }, [
    amount,
    currency,
    currentRate,
    quoteData?.amountReceived,
    quoteData?.fiatAmount,
    zar,
  ]);

  const shortenAddress = (address) =>
    address.slice(0, 4) + "..." + address.slice(-5);

  const handleLocalBack = () => {
    if (onCancel) {
      onCancel();
    }
  };

  const handleTwoFA = () => {
    if (!isEnabled) {
      setOpen(true);
    }
    if (isEnabled) {
      onConfirm();
    }
  };
  const handleSetUp = () => {
    if (setUp) {
      setUp();
    }
  };
  return (
    <>
      {!open ? (
        <Card className="w-full lg:pt-20 pt-14 lg:px-16 px-10 bg-white !shadow-none rounded-2xl relative">
          <div className="text-center h-[66px] text-[24px] font-semibold text-[#423E51] bg-[#FCD8E54D] rounded-2xl mb-6 flex items-center justify-center gap-3 mx-5">
            Send Confirmation
          </div>

          <CardContent className="px-0 lg:px-6 mt-6 w-full">
            <div className="flex flex-col lg:grid lg:grid-cols-3 gap-4 lg:grid-rows-1">
              <div className="space-y-2">
                <label className="text-gray-600 text-sm">You are sending</label>
                <Button
                  variant="outline"
                  className="w-full flex items-center justify-between py-4 lg:py-8 rounded-2xl transition-colors h-auto lg:h-[88px] px-4 lg:px-6"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 lg:w-10 h-8 lg:h-[88px] rounded-full flex items-center justify-center">
                      <img
                        src={asset.assetIcon}
                        alt={asset.assetName}
                        width={32}
                        height={32}
                        className="lg:w-10 lg:h-10 text-white"
                      />
                    </div>
                    <div className="text-left">
                      <div className="font-semibold text-sm lg:text-base">
                        {asset.asset.toUpperCase()}
                      </div>
                      <p className="text-xs lg:text-sm text-gray-600">
                        {asset?.assetName}
                      </p>
                    </div>
                  </div>
                </Button>
              </div>
              <div className="space-y-2 flex flex-col lg:col-span-2">
                <label className="text-[#6D6584] text-sm">Amount</label>
                <div className="py-3 lg:py-4 px-4 lg:px-6 rounded-2xl bg-gray-50 flex-1 flex items-center">
                  <div className="text-lg lg:text-xl font-semibold text-[#6D6584] flex flex-col lg:flex-row lg:justify-between lg:items-center w-full gap-2 lg:gap-0">
                    <p className="text-center lg:text-left">
                      {quoteData?.amountReceivedFormatted}
                    </p>
                    <p className="text-center lg:text-right">
                      ≈ {formattedConvertedValue}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <h3 className="text-sm font-semibold text-[#6D6584] mb-3 mt-6 lg:mt-8">
              Transaction details
            </h3>

            <div className="border rounded-lg divide-y">
              <div className="p-3 lg:p-4 flex justify-between">
                <span className="text-sm text-gray-700">You Send</span>
                <span className="text-sm font-bold text-gray-800">
                  {quoteData?.amountReceivedFormatted}
                </span>
              </div>
              {typeof destinationAddress === "object" ? (
                <div className="p-3 lg:p-4 flex justify-between">
                  <span className="text-sm text-gray-700">Send to</span>
                  <span className="text-sm font-medium text-gray-800 text-right">
                    <p className="text-gray-800 font-semibold">
                      {destinationAddress?.firstName}{" "}
                      {destinationAddress?.lastName}
                    </p>
                    <p className="text-sm text-gray-600">
                      {shortenAddress(destinationAddress?.walletAddress)}
                    </p>
                  </span>
                </div>
              ) : (
                <div className="p-3 lg:p-4 flex justify-between">
                  <span className="text-sm text-gray-700">Send to</span>
                  <span className="text-sm font-medium text-gray-800 text-right">
                    <p className="text-sm text-gray-600 break-all">
                      {destinationAddress}
                    </p>
                  </span>
                </div>
              )}
              <div className="p-3 lg:p-4 flex justify-between items-center">
                <span className="flex items-center gap-1 text-sm text-gray-700">
                  Service charge
                  <Info size={14} className="text-gray-500" />
                </span>
                <span className="text-sm font-medium text-gray-800">
                  {quoteData?.withdrawalFeeFormatted ?? "—"}
                </span>
              </div>
              <div className="p-3 lg:p-4 flex justify-between">
                <span className="text-sm text-gray-700 font-bold">
                  Total cost
                </span>
                <span className="text-sm font-bold text-gray-900">
                  {quoteData?.totalAmountFormatted}
                </span>
              </div>
            </div>
          </CardContent>

          <div className="flex justify-center w-full">
            <div className="flex flex-col lg:flex-row items-center justify-center lg:mr-10 w-full lg:w-auto gap-4 px-4 lg:px-0">
              <button
                type="button"
                onClick={handleLocalBack}
                className={`p-3 md:p-4 flex items-center justify-center gap-2 border border-gray-200 rounded-full w-full lg:w-auto`}
              >
                <ArrowLeft size={20} className="text-text-secondary" />
                <div className="block lg:hidden text-text-secondary">Back</div>
              </button>
              <Button
                onClick={handleTwoFA}
                className={`text-white px-6 py-4 rounded-full font-semibold transition-opacity w-full lg:min-w-[302px] h-[48px] ${
                  disableBtn || isConfirming
                    ? " !bg-gray-400 cursor-not-allowed"
                    : " !bg-[linear-gradient(86.72deg,#57249C_-10.66%,#EB3366_113.47%)] hover:opacity-90"
                }`}
                disabled={disableBtn}
                isLoading={isConfirming}
              >
                {isConfirming ? "Confirming..." : "Confirm"}
              </Button>
            </div>
          </div>

          <SecuredFooter />
        </Card>
      ) : (
        <TwoFA
          isOpen={isTwoFAOpen}
          closeOverlay={() => setIsTwoFAOpen(false)}
          isSetUp={handleSetUp}
        />
      )}
    </>
  );
};

export default SendConfirmation;
